import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ListboxComponent, ListboxConfig, ListboxItem, ListboxTab } from '@groundworks/ngx-groundworks-ui';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../config.service';
import { BranchInfo, PendingSaleInfo } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';
import { DefaultBranchService } from '../../services/default-branch.service';
import { OpportunityRevisitsSignalRService } from '../../services/opportunity-revisits-signalr.service';
import { PendingSalesSignalRService } from '../../services/pending-sales-signalr.service';
import { PendingSalesService } from '../../services/pending-sales.service';
import { mapPendingSaleToListboxItem, removeListboxItemById } from '../../utils/listbox.util';
import { SideModalComponent } from '../side-modal/side-modal.component';

@Component({
  selector: 'app-pending-sales-listbox',
  imports: [ListboxComponent, SideModalComponent],
  templateUrl: './pending-sales-listbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PendingSalesListboxComponent implements OnInit, OnDestroy {
  @ViewChild('sideModal') sideModalComponent!: SideModalComponent;
  private readonly configService = inject(ConfigService);
  private readonly pendingSalesService = inject(PendingSalesService);
  private readonly branchService = inject(BranchService);
  private readonly defaultBranchService = inject(DefaultBranchService);
  private readonly destroy$ = new Subject<void>();
  private readonly removedOpportunityIds = new Set<string>();
  private readonly pendingSalesSignalRService = inject(PendingSalesSignalRService);
  private readonly opportunityRevisitsSignalRService = inject(OpportunityRevisitsSignalRService);
  private readonly startDate = signal<Date | undefined>(undefined);
  private readonly endDate = signal<Date | undefined>(undefined);

  get serviceEnabled(): boolean {
    return this.configService.enableService;
  }

  selectedBranches = signal<BranchInfo[]>([]);
  pendingSales: ListboxItem[] = [];
  completedSales: ListboxItem[] = [];
  serviceSalesToApprove: ListboxItem[] = [];

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  showSettings = signal(true);
  hasLoadedData = signal(false);

  tableConfig = this.buildTableConfig('CFI / Sales Rep');
  serviceTableConfig = this.buildTableConfig('Service Tech');
  completedTableConfig = this.buildTableConfig('CFI/Service Tech', true);

  tabs = signal<ListboxTab[]>([]);

  readonly noDataMessage = computed(() => {
    if (this.selectedBranches().length === 0 || !this.hasLoadedData()) {
      return 'Please select a branch';
    }
    return 'No records found';
  });

  private initialLoadHasHappened = false;
  private hasDefaultBranch = signal(false);

  private buildTableConfig(repColumnHeader: string, includeAppointmentType = false): ListboxConfig {
    return {
      columns: [
        { header: 'Branch', field: 'branch', sortable: true },
        { header: 'Customer Name', field: 'name', sortable: true },
        ...(includeAppointmentType
          ? [{ header: 'Appointment Type', field: 'appointmentType', sortable: true }]
          : []),
        { header: 'Sale Date', field: 'saleDate', sortable: true, dataType: 'date' },
        { header: 'Sale Amount', field: 'saleAmount', sortable: true, dataType: 'currency' },
        { header: repColumnHeader, field: 'cfiName', sortable: true },
        { header: 'Actions', field: 'action', sortable: false },
      ],
    };
  }

  ngOnInit(): void {
    this.defaultBranchService.defaultBranch$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasDefault) => {
        this.hasDefaultBranch.set(hasDefault);
        if (hasDefault && this.selectedBranches().length > 0 && !this.initialLoadHasHappened) {
          this.loadPendingSales();
          this.initialLoadHasHappened = true;
        }
      });

    this.branchService
      .getSelectedBranches()
      .pipe(takeUntil(this.destroy$))
      .subscribe((branches) => this.handleBranchSelectionChange(branches));

    this.pendingSalesSignalRService.startConnection();
    this.pendingSalesSignalRService.pendingSaleCompleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((opportunityId) => {
        this.removePendingSale(opportunityId);
      });

    this.opportunityRevisitsSignalRService.startConnection();
    this.opportunityRevisitsSignalRService.opportunityUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((opportunityId) => {
        this.removePendingSale(opportunityId);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.opportunityRevisitsSignalRService.stopConnection();
  }

  private loadPendingSales(): void {
    const branches = this.selectedBranches();
    if (branches.length === 0) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.pendingSalesService
      .getPendingSales(branches.map(branch => branch.id!), this.startDate(), this.endDate())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sales) => {
          this.pendingSales = sales.pendingSales?.map(sale => this.toListboxItem(sale)) ?? [];
          this.completedSales = sales.completedSales?.map(sale => this.toListboxItem(sale)) ?? [];
          this.serviceSalesToApprove = sales.serviceSalesToApprove?.map(sale => this.toListboxItem(sale)) ?? [];
          this.removedOpportunityIds.clear();
          this.updateTabs();
          this.isLoading.set(false);
          this.hasLoadedData.set(true);
        },
        error: (error) => {
          console.error('Error loading pending sales:', error);
          this.errorMessage.set('Failed to load pending sales');
          this.isLoading.set(false);
          this.hasLoadedData.set(true);
        },
      });
  }

  private updateTabs(): void {
    this.tabs.set([
      {
        id: 1,
        label: 'Sales to Approve',
        config: this.tableConfig,
        items: this.pendingSales
      },
      ...(this.serviceEnabled ? [{
        id: 2,
        label: 'Service Sales to Approve',
        config: this.serviceTableConfig,
        items: this.serviceSalesToApprove
      }] : []),
      {
        id: 3,
        label: 'Completed',
        config: this.completedTableConfig,
        items: this.completedSales
      }
    ]);
  }

  private removePendingSale(opportunityId: string): void {
    if (!opportunityId || this.removedOpportunityIds.has(opportunityId)) {
      console.warn('Pending sale already removed:', opportunityId);
      return;
    }

    const removedFromPending = removeListboxItemById(this.pendingSales, opportunityId);
    const removedFromService = removeListboxItemById(this.serviceSalesToApprove, opportunityId);
    const removedFromCompleted = removeListboxItemById(this.completedSales, opportunityId);

    if (removedFromPending || removedFromService || removedFromCompleted) {
      this.removedOpportunityIds.add(opportunityId);
      this.updateTabs();
    }
  }

  private toListboxItem(sale: PendingSaleInfo): ListboxItem {
    return mapPendingSaleToListboxItem(sale, this.configService.appBaseUrl);
  }

  private handleBranchSelectionChange(branches: BranchInfo[]): void {
    if (branches.length > 0) {
      this.selectedBranches.set(branches);
      this.hasLoadedData.set(false);

      if (!this.initialLoadHasHappened && this.hasDefaultBranch()) {
        this.loadPendingSales()
        this.initialLoadHasHappened = true;
      }
    } else {
      this.selectedBranches.set([]);
      this.hasLoadedData.set(false);
    }
  }

  handleAction(actionValue: string): void {
    window.open(actionValue, '_blank');
  }

  handleSettingsClick(): void {
    this.sideModalComponent.open(this.sideModalComponent.sideModalTemplate);
  }

  onFiltersChanged(filters: { startDate?: Date; endDate?: Date }): void {
    this.startDate.set(filters.startDate);
    this.endDate.set(filters.endDate);
    this.loadPendingSales();
    this.initialLoadHasHappened = true;
  }
}
